import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { setMockSession } from "../auth/auth";
import { Button, Card, LoadingState, PageHeader } from "../components/ui";
import { clientIdForHost, isMock } from "../config";
import { Logo, useBranding } from "../lib/branding";
import { MOCK_ME } from "../mocks/fixtures";

/**
 * One quiet card: the product's name, one action. Managed login does the
 * rest on `auth.<zone>` — password + authenticator, or a passkey — so
 * nothing here collects a credential. In mock builds, two personas.
 *
 * One centred column at the card's width, because the next page is one
 * too: managed login centres its form (owner, 2026-09-22; gairm/lms#225),
 * so the logo, the heading and the button sit on the axis the eye is
 * about to keep. Centred across, not down — the shell has a rail and a
 * footer, and centring down clips the top of a column taller than the
 * viewport, which is the 320 px at x-large text case.
 */
export function LoginPage() {
  const { status, signIn, config } = useAuth();
  const branding = useBranding();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? "/";
  if (status === "signed-in") return <Navigate to={from} replace />;
  if (status === "loading") return <LoadingState label="Signing in…" />;
  const canSignIn = isMock || !!clientIdForHost(config);
  return (
    <div className="mx-auto w-full max-w-md">
      {/* The full logo — the tenant's mark with its name beside it (TEN-07) — above the one thing to do. */}
      <p className="mb-6 text-center">
        <Logo branding={branding} size="lg" />
      </p>
      <PageHeader align="center" title="Sign in" subtitle="You will be asked for your password and authenticator app, or your passkey." />
      <Card>
        {isMock ? (
          <div className="space-y-3">
            <p className="text-sm text-muted">Mock sign-in (local development and tests).</p>
            <Button
              variant="primary"
              className="w-full"
              onClick={() => {
                setMockSession(MOCK_ME.apprentice);
                window.location.assign(from);
              }}
            >
              Continue as apprentice
            </Button>
            <Button
              className="w-full"
              onClick={() => {
                setMockSession(MOCK_ME["staff-sso"]);
                window.location.assign(from);
              }}
            >
              Continue as staff (SSO)
            </Button>
            <Button
              className="w-full"
              onClick={() => {
                setMockSession(MOCK_ME["tenant-admin"]);
                window.location.assign(from);
              }}
            >
              Continue as tenant admin
            </Button>
          </div>
        ) : canSignIn ? (
          <Button variant="primary" className="w-full" onClick={() => void signIn(from)}>
            Sign in
          </Button>
        ) : (
          <p className="text-muted">This address is not a tenant's sign-in page. Use the address you were given.</p>
        )}
      </Card>
    </div>
  );
}
